import {useFieldArray, useFormContext} from 'react-hook-form';
import {
  FolderValues,
  isGeneralRegulationFolder,
  NetworkDetailsFormValues,
  sortFoldersWithGeneralFirst,
  NetworkNodeFieldPath,
} from '../../../types';
import {SVG} from '../../../../../../../constants/assetLists';
import {useEffect, useRef, useState} from 'react';
import AppConfirmationDialog from '../../../../../../../components/common/AppConfirmationDialog';
import {getFolders} from './defaultFolders';
import AddFolderDialog from '../checklists/AddFolderDialog';

type Props = {
  selectedFieldPath: NetworkNodeFieldPath;
  selectedItem: string | null;
  onSelectFolder: (id: string) => void;
  getRegulationsCount: (id: string) => number;
};

function RegulationsSidebar({
  selectedFieldPath,
  selectedItem,
  onSelectFolder,
  getRegulationsCount,
}: Props) {
  const [showAddFolderDialog, setShowAddFolderDialog] = useState(false);
  const [selectedFolderId, setSelectedFolderId] = useState<string | null>(null);
  const [selectedFolderIndex, setSelectedFolderIndex] = useState<number | null>(
    null,
  );
  const [showConfirmationDialog, setShowConfirmationDialog] = useState(false);

  const {control, watch} = useFormContext<NetworkDetailsFormValues>();
  const {
    fields: folderFields,
    append,
    remove,
    replace,
  } = useFieldArray({
    control,
    name: `${selectedFieldPath}.regulationFolders`,
    keyName: 'fieldKey',
  });
  const {replace: replaceRegulation} = useFieldArray({
    control,
    name: `${selectedFieldPath}.regulations`,
    keyName: 'id',
  });

  const regulations = watch(`${selectedFieldPath}.regulations`) || [];
  const regulationFolders =
    watch(`${selectedFieldPath}.regulationFolders`) || [];

  const didInitRef = useRef(false);

  useEffect(() => {
    if (didInitRef.current) return;

    if (folderFields.length === 0) {
      replace(getFolders());
    }

    didInitRef.current = true;
  }, [folderFields.length, replace]);

  const handleAddFolder = (name: string) => {
    append({id: crypto.randomUUID(), name});
  };

  const handleRemoveFolder = (idx: number, folder: FolderValues) => {
    if (isGeneralRegulationFolder(folder)) return;

    if (getRegulationsCount(folder.id) === 0) {
      remove(idx);
    } else {
      setSelectedFolderIndex(idx);
      setSelectedFolderId(folder.id);
      setShowConfirmationDialog(true);
    }
  };

  const handleCloseConfirmationDialog = () => {
    setSelectedFolderIndex(null);
    setSelectedFolderId(null);
    setShowConfirmationDialog(false);
  };

  const handleRemoveFolderRegulations = () => {
    if (!selectedFolderId || selectedFolderIndex === null) return;

    replaceRegulation(
      regulations.filter(
        regulation => regulation.folderId !== selectedFolderId,
      ),
    );
    remove(selectedFolderIndex);
    handleCloseConfirmationDialog();
  };

  return (
    <>
      <div className="w-[255px] h-full overflow-y-auto bg-[#F4F4F4] border-l border-l-[#5F6368] p-1 flex flex-col gap-3">
        <div className="px-3 py-2 border-b border-b-lightDarkGray flex items-center justify-between">
          <p className="m-0 text-xl font-light">תיקיות רגולציות</p>

          <button
            type="button"
            className="w-8 h-8 bg-white border border-gray70 rounded-lg flex items-center justify-center self-center shadow-sm"
            onClick={() => setShowAddFolderDialog(true)}>
            <img
              src={SVG.addDarkBlueIcon}
              alt="add"
              className="w-[14px] h-[14px] object-contain"
            />
          </button>
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto px-2">
          <div className="flex flex-col gap-2">
            {sortFoldersWithGeneralFirst(regulationFolders).map(
              ({folder, originalIndex: idx}) => (
              <div
                key={folder.id}
                role="button"
                tabIndex={0}
                aria-selected={selectedItem === folder.id}
                onClick={() => onSelectFolder(folder.id)}
                onKeyDown={e => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onSelectFolder(folder.id);
                  }
                }}
                className={`${isGeneralRegulationFolder(folder) ? 'pb-2 border-b border-b-tealBlue' : ''}`}>
                <div
                  className={`flex items-center justify-between ps-3 pe-2 py-1 rounded-tl-[30px] rounded-bl-[30px] rounded-tr-[6px] rounded-br-[6px] transition-colors duration-200 group hover:bg-softAqua
                  ${selectedItem === folder.id ? 'bg-softAqua' : ''}`}>
                  <div className="flex items-center gap-2.5">
                    <div className="w-5 h-4">
                      <img
                        src={SVG.folderBlackIcon}
                        alt="clock"
                        className="w-full h-full object-contain"
                      />
                    </div>

                    <span className="text-base text-black80">
                      {folder.name}
                    </span>
                  </div>

                  <div className="flex items-center gap-2">
                    <span className="text-base text-black80">
                      {getRegulationsCount(folder.id)}
                    </span>

                    {!isGeneralRegulationFolder(folder) && (
                      <button
                        type="button"
                        className="w-2.5 h-2.5 hidden group-hover:block"
                        onClick={e => {
                          e.stopPropagation();
                          handleRemoveFolder(idx, folder);
                        }}>
                        <img
                          src={SVG.closeDarkIcon}
                          className="w-full h-full object-contain"
                          alt="remove"
                        />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ),
            )}
          </div>
        </div>
      </div>

      <AddFolderDialog
        open={showAddFolderDialog}
        onClose={() => setShowAddFolderDialog(false)}
        onSave={handleAddFolder}
      />

      <AppConfirmationDialog
        open={showConfirmationDialog}
        onClose={handleCloseConfirmationDialog}
        icon={
          <img src={SVG.deleteRedIcon} alt="" className="w-[32px] h-[32px]" />
        }
        title="מחיקה"
        subTitle={'בתיקיה יש קבצים, האם למחוק?'}
        confirmBtnText="כן"
        cancelBtnText="לא"
        onConfirm={handleRemoveFolderRegulations}
        cancelBtnTextClasses="!text-darkGray"
        bordered={false}
      />
    </>
  );
}

export default RegulationsSidebar;
